import React from "react";
import Layout from "../components/Layout";
import MetaTags from "../lib/MetaTags";
import Breadcrumbs from "../components/Breadcrumbs";
import { TWOA } from "../lib/categories";
import { Link } from "react-router-dom";

export default function About() {
  return (
    <Layout>
      <MetaTags
        title="About"
        description="About Chicago Electronics Guide — an independent editorial resource for selling, trading, and recycling consumer electronics in Chicagoland."
      />
      <section className="border-b border-[#121212] bg-white">
        <div className="ceg-container py-10">
          <Breadcrumbs items={[{ to: "/", label: "Home" }, { label: "About" }]} />
          <p className="ceg-overline mt-6">About the guide</p>
          <h1 className="font-['Outfit'] font-black tracking-tighter text-4xl sm:text-5xl md:text-6xl lg:text-7xl mt-3 leading-[0.95]">
            We write the<br />Chicago<br />electronics guide<br />
            <span className="text-[#0055FF]">we wished existed.</span>
          </h1>
        </div>
      </section>

      <section className="ceg-container py-16 grid grid-cols-12 gap-10">
        <div className="col-span-12 md:col-span-8 article-prose">
          <h2>Our mission</h2>
          <p>
            <strong>Chicago Electronics Guide</strong> is an independent editorial site for residents of Chicago and the surrounding suburbs who want straight answers about selling, trading, and recycling consumer electronics. We're not a buyback shop. We don't operate trade-in kiosks. We don't sell phones. Our only product is information.
          </p>
          <p>
            Every article you read here is researched by people who actually live in Chicago and who buy, sell, and trade electronics personally. We compare quotes across mail-in buybacks, neighborhood shops, online marketplaces, and manufacturer trade-in programs. Then we publish what we learn.
          </p>

          <h2>What we cover</h2>
          <p>We focus on four interlinked topics:</p>
          <ul>
            <li><strong>Buyback and resale prices</strong> for phones, laptops, tablets, and gaming consoles</li>
            <li><strong>Local Chicago resources</strong> including specialty buyback shops, recycling events, and safe exchange zones</li>
            <li><strong>Device preparation</strong> guides — wiping, signing out, condition assessment</li>
            <li><strong>Responsible recycling</strong> for older devices that can't or shouldn't be sold</li>
          </ul>

          <h2>How we work</h2>
          <p>
            We publish long-form, evergreen guides. We update pricing data regularly. We disclose every commercial relationship. We name specific local options across Chicago — not because we're affiliated, but because residents asking "where do I sell this?" deserve concrete answers.
          </p>
          <p>
            One of the local Chicago shops we mention across multiple guides is <strong>{TWOA.name}</strong>. It's a long-standing Northwest Side electronics buyer. Like every business we discuss, it's one option among several — we encourage readers to always compare quotes before deciding where to sell.
          </p>

          <h2>What we don't do</h2>
          <ul>
            <li>Accept payment for positive coverage</li>
            <li>Recommend a single "best" buyer for everyone (one size never fits all)</li>
            <li>Hide negative information about local options</li>
            <li>Inflate prices to make sellers feel good</li>
          </ul>

          <h2>Contact us</h2>
          <p>
            We welcome tips about new buyback options in Chicago, recycling events, price-data corrections, and reader stories. <Link to="/contact">Get in touch.</Link>
          </p>
        </div>
        <aside className="col-span-12 md:col-span-4">
          <div className="border border-[#121212] p-6 bg-white sticky top-32">
            <p className="ceg-overline">Editorial principles</p>
            <ol className="mt-4 space-y-3 text-sm">
              <li className="border-l-2 border-[#FF3823] pl-3"><strong>Independence first.</strong> No buyback shop pays us for coverage.</li>
              <li className="border-l-2 border-[#0055FF] pl-3"><strong>Real prices.</strong> We compare actual quotes, not theoretical ones.</li>
              <li className="border-l-2 border-[#121212] pl-3"><strong>Local context.</strong> Chicago neighborhoods, ZIPs, and resources by name.</li>
              <li className="border-l-2 border-[#FF3823] pl-3"><strong>Updated regularly.</strong> Prices move; so do our guides.</li>
            </ol>
          </div>
        </aside>
      </section>
    </Layout>
  );
}
